import Spinner from "components/Spinner";
import { useEffect, useState } from "react";
import authHeader from "services/auth-header";
import API from "services/axios";
import UserSmtpList from "./UserSmtpList";
import { setNotification } from "features/Notification/notificationSlice";
import { useDispatch } from "react-redux";

export default function UserLocationSmtp({ selectedUser }) {
  const dispatch = useDispatch();
  /* ******************** All States ************************* */
  const [loading, setLoading] = useState(false);
  const [locationsList, setLocationsList] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSmtpItem, setSelectedSmtpItem] = useState(null);
  const [selectedSmtpLocations, setSelectedSmtpLocations] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [userSmtpList, setUserSmtpList] = useState(null);

  /* ******************** All Functions ************************* */
  // get All Locations
  const getAllLocations = async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/locations", { headers: authHeader() });
      setLocationsList(data);
      if (data?.length > 0) {
        setSelectedLocation(data[0].id);
      }
      setLoading(false);
    } catch (err) {
      alert(err.message);
      setLoading(false);
    }
  };

  const getUserSmtpList = async () => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/user/smtp-list?limit=10000000&page=1",
        { userId: selectedUser.id },
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        // Handle success scenario

        setUserSmtpList(data?.data || []);
      } else {
        throw new Error(data?.message || "Failed to fetch user SMTP list");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const handleSelectSmtp = (smtpItem) => {
    setSelectedSmtpItem(smtpItem);
    // Set selected locations from the smtp item
    if (smtpItem.locationIdList && Array.isArray(smtpItem.locationIdList)) {
      setSelectedSmtpLocations(smtpItem.locationIdList);
    } else if (smtpItem.locations && Array.isArray(smtpItem.locations)) {
      setSelectedSmtpLocations(smtpItem.locations.map((loc) => loc.id));
    } else {
      setSelectedSmtpLocations([]);
    }
  };

  const toggleLocationSelection = (locationId) => {
    setSelectedSmtpLocations((prev) => {
      if (prev.includes(locationId)) {
        return prev.filter((id) => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  const handleUpdateLocations = async () => {
    if (!selectedSmtpItem) {
      alert("Please select an SMTP item first");
      return;
    }
    try {
      setUpdateLoading(true);
      const { data } = await API.patch(
        `/user/smtp/`,
        {
          id: selectedSmtpItem.id,
          locationIdList: selectedSmtpLocations,
        },
        {
          headers: authHeader(),
        },
      );
      if (data?.success) {
        setSelectedSmtpItem({
          ...selectedSmtpItem,
          locationIdList: selectedSmtpLocations,
        });
        getAllLocations(); // Refresh locations list after update
        getUserSmtpList(); // Refresh user SMTP list after update
        // Update the selected item with new locations
        dispatch(
          setNotification({
            message: data?.message || "Brands updated successfully",
            type: "success",
          }),
        );
      } else {
        throw new Error(data?.message || "Failed to update brands");
      }
      setUpdateLoading(false);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      setUpdateLoading(false);
    }
  };

  /* ******************** All UseEffects ************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      getAllLocations();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div>
      {loading && <Spinner />}
      <div className="row ">
        <div className="col-md-8">
          {/* User Info */}
          <UserSmtpList
            selectedUser={selectedUser}
            selectedLocation={selectedLocation}
            locationsList={locationsList}
            onSelectSmtp={handleSelectSmtp}
            selectedSmtpItem={selectedSmtpItem}
            userSmtpList={userSmtpList}
            setUserSmtpList={setUserSmtpList}
            getUserSmtpList={getUserSmtpList}
          />
        </div>
        <div className="col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <h5 className="border-bottom pb-2 mb-3">
                {selectedSmtpItem
                  ? `Brands for ${selectedSmtpItem.smtpName}`
                  : "Select an SMTP item"}
              </h5>
              {selectedSmtpItem ? (
                <>
                  <div
                    className="border rounded p-2 mb-3"
                    style={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      backgroundColor: "#f8f9fa",
                    }}
                  >
                    {locationsList && locationsList.length > 0 ? (
                      locationsList.map((location) => (
                        <div key={location.id} className="form-check mb-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id={`smtp-location-${location.id}`}
                            checked={selectedSmtpLocations.includes(
                              location.id,
                            )}
                            onChange={() =>
                              toggleLocationSelection(location.id)
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`smtp-location-${location.id}`}
                            style={{ cursor: "pointer" }}
                          >
                            {location.locationName ||
                              location.name ||
                              `Brand ${location.id}`}
                          </label>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted mb-0">No brands available</p>
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      {selectedSmtpLocations.length} location(s) selected
                    </small>
                    <button
                      onClick={handleUpdateLocations}
                      className="btn btn-sm btn-primary"
                      disabled={updateLoading}
                    >
                      {updateLoading ? "Updating..." : "Update Brands"}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-muted text-center mt-4">
                  Click on an SMTP row to view and update its brands
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
